import type { ColumnFilterValue } from '@/models/flowFilter.type';
import { DATE_FORMAT } from '@/shared/utils/date-utils';
import { Group, Text } from '@mantine/core';
import { DatePickerInput, type DatesRangeValue, type DateStringValue } from '@mantine/dates';
import { useId } from '@mantine/hooks';
import { IconCalendar } from '@tabler/icons-react';
import { useTranslate } from '@tolgee/react';
import isEqual from 'lodash/isEqual';
import { useState } from 'react';

type PeriodRange = DatesRangeValue<DateStringValue>;

type PeriodFilterProps = {
  value: ColumnFilterValue | undefined;
  onChange: (value: ColumnFilterValue | undefined) => void;
  /** Libellé du champ ; « Période » par défaut. */
  label?: string;
};

const toRange = (value: ColumnFilterValue | undefined): PeriodRange =>
  value?.kind === 'date-range' ? [value.from ?? null, value.to ?? null] : [null, null];

/**
 * Sélecteur de période (jour de début et jour de fin inclus), au format d'un filtre `date-range`.
 * La période n'est transmise qu'une fois ses deux bornes choisies, ou vidée.
 */
const PeriodFilter = ({ value, onChange, label }: PeriodFilterProps) => {
  const { t } = useTranslate();
  const inputId = useId();
  const [range, setRange] = useState<PeriodRange>(() => toRange(value));
  const [syncedValue, setSyncedValue] = useState(value);

  // Resynchronise la sélection si la période change de l'extérieur (ex. réinitialisation).
  if (!isEqual(value, syncedValue)) {
    setSyncedValue(value);
    setRange(toRange(value));
  }

  const handleChange = (next: PeriodRange) => {
    setRange(next);
    const [from, to] = next;
    if (from && to) onChange({ kind: 'date-range', from, to });
    else if (!from && !to) onChange(undefined);
  };

  // Libellé à gauche du champ : rendu hors de `DatePickerInput` et relié par `htmlFor`.
  return (
    <Group gap="sm" wrap="nowrap">
      <Text component="label" htmlFor={inputId} size="sm" fw={500}>
        {label ?? t('common.period.label')}
      </Text>
      <DatePickerInput
        id={inputId}
        type="range"
        clearable
        allowSingleDateInRange
        placeholder={t('common.period.placeholder')}
        leftSection={<IconCalendar size={16} stroke={1.5} aria-hidden />}
        value={range}
        onChange={handleChange}
        valueFormat={DATE_FORMAT}
        flex={{ base: 1, sm: 'none' }}
        w={{ sm: 300 }}
      />
    </Group>
  );
};

export default PeriodFilter;
